import { toast } from '../../common_components/Toast/ToastUtils';
import XLSX from 'xlsx-js-style';
import moment from 'moment';
import { formatStat } from '../../helper/statsFormatter';
// ═══════════════════════════════════════════════════════════
// ✅ STYLE DEFINITIONS
// ═══════════════════════════════════════════════════════════
const titleStyle = {
    font: { bold: true, sz: 18, color: { rgb: "181D27" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        bottom: { style: "thin", color: { rgb: "E5E7EB" } }
    }
};
const dateRangeStyle = {
    font: { bold: false, sz: 12, color: { rgb: "6B7280" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        bottom: { style: "medium", color: { rgb: "D1D5DB" } }
    }
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "thin", color: { rgb: "D1D5DB" } }
    }
};
const nameHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: "374151" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "medium", color: { rgb: "D1D5DB" } }
    }
};
const dataRowStyle = {
    font: { bold: false, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    alignment: { horizontal: "right", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } }
    }
};
const nameColumnStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "medium", color: { rgb: "D1D5DB" } }
    }
};
const summaryRowStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "right", vertical: "center" },
    border: {
        top: { style: "medium", color: { rgb: "D1D5DB" } },
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "thin", color: { rgb: "D1D5DB" } }
    }
};
const summaryNameStyle = {
    font: { bold: true, sz: 11, color: { rgb: "181D27" } },
    fill: { fgColor: { rgb: "F3F4F6" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
        top: { style: "medium", color: { rgb: "D1D5DB" } },
        bottom: { style: "medium", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "medium", color: { rgb: "D1D5DB" } }
    }
};
const emptyStyle = {
    fill: { fgColor: { rgb: "FFFFFF" } }
};
// ═══════════════════════════════════════════════════════════
// ✅ HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════
const formatMetricLabel = (key) => {
    if (!key)
        return '';
    const dictionary = {
        gross_sales: "GROSS SALES",
        total_cost: "TOTAL COST",
        outboundclicks: "OUTBOUND CLICKS",
        avg_engagement_time_per_session: "AVG ENG TIME",
    };
    if (dictionary[key.toLowerCase()]) {
        return dictionary[key.toLowerCase()];
    }
    return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toUpperCase();
};
const formatValue = (value, key) => {
    if (value === undefined || value === null)
        return '-';
    try {
        if (typeof formatStat === 'function') {
            const result = formatStat(value, key);
            return result !== undefined && result !== null ? String(result) : '-';
        }
    }
    catch (e) {
        console.warn('formatStat error:', e);
    }
    if (typeof value === 'number') {
        if (key && (key.includes('revenue') || key.includes('cost') || key.includes('sales') || key.includes('spend') || key.includes('aov'))) {
            return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        if (key && (key.includes('rate') || key.includes('roas') || key.includes('ctr') || key.includes('cvr'))) {
            return `${value.toFixed(2)}%`;
        }
        if (key && (key.includes('order') || key.includes('session') || key.includes('click') || key.includes('impression'))) {
            return Math.round(value).toLocaleString();
        }
        return String(value);
    }
    return String(value);
};
// ✅ Get visible metrics based on preferences
const getVisibleMetrics = (allMetrics, preferences, networkKey) => {
    if (!allMetrics || allMetrics.length === 0) {
        return [];
    }
    if (!preferences?.[networkKey] || preferences[networkKey].length === 0) {
        return allMetrics;
    }
    const networkPrefs = preferences[networkKey];
    const visibilityMap = new Map();
    const orderMap = new Map();
    networkPrefs.forEach((item, index) => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        visibilityMap.set(metricKey, config?.visibility !== false);
        orderMap.set(metricKey, config?.order ?? index + 1);
    });
    const visibleMetrics = allMetrics.filter(metric => {
        const isVisible = visibilityMap.get(metric);
        return isVisible === undefined ? true : isVisible;
    });
    visibleMetrics.sort((a, b) => {
        const orderA = orderMap.get(a) ?? 999;
        const orderB = orderMap.get(b) ?? 999;
        return orderA - orderB;
    });
    return visibleMetrics;
};
// ═══════════════════════════════════════════════════════════
// ✅ MAIN EXPORT FUNCTION
// ═══════════════════════════════════════════════════════════
export const exportEmailChannelExcel = (title, networkKey, allMetrics, rows, options) => {
    // ✅ Validation with specific error messages
    if (!rows) {
        console.error('❌ Export Error: rows is undefined');
        toast.info('No data available to export.');
        return;
    }
    if (!Array.isArray(rows)) {
        console.error('❌ Export Error: rows is not an array', typeof rows);
        toast.info('No data available to export.');
        return;
    }
    if (rows.length === 0) {
        console.error('❌ Export Error: rows array is empty');
        toast.info('No data available to export.');
        return;
    }
    if (!allMetrics || allMetrics.length === 0) {
        console.error('❌ Export Error: no metrics found');
        toast.info('No metrics available to export');
        return;
    }
    try {
        const allExcelRows = [];
        const merges = [];
        // ✅ Get visible metrics based on preferences
        const preferences = options?.preferences || null;
        const visibleMetrics = getVisibleMetrics(allMetrics, preferences, networkKey);
        if (visibleMetrics.length === 0) {
            console.error('❌ Export Error: no visible metrics');
            toast.info('No visible columns to export. Please make sure at least one column is visible.');
            return;
        }
        const maxColumns = Math.max(visibleMetrics.length + 1, 3);
        // ═══════════════════════════════════════════════════════════
        // 0️⃣ TOP HEADER SECTION
        // ═══════════════════════════════════════════════════════════
        // Row 0: Title
        const titleRow = [];
        const safeTitle = title || 'Report';
        for (let i = 0; i < maxColumns; i++) {
            titleRow.push({
                v: i === 0 ? `${safeTitle} Report` : '',
                t: 's',
                s: titleStyle
            });
        }
        allExcelRows.push(titleRow);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: maxColumns - 1 } });
        // Row 1: Date Range
        let dateRangeText = '';
        if (options?.startDate && options?.endDate) {
            try {
                const startFormatted = moment(options.startDate).format('MM/DD/YYYY');
                const endFormatted = moment(options.endDate).format('MM/DD/YYYY');
                dateRangeText = `${startFormatted} - ${endFormatted}`;
            }
            catch (e) {
                console.warn('Date formatting error:', e);
                dateRangeText = `${options.startDate} - ${options.endDate}`;
            }
        }
        const dateRow = [];
        for (let i = 0; i < maxColumns; i++) {
            dateRow.push({
                v: i === 0 ? dateRangeText : '',
                t: 's',
                s: dateRangeStyle
            });
        }
        allExcelRows.push(dateRow);
        merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: maxColumns - 1 } });
        // Row 2: Spacer
        const spacerRow = [];
        for (let i = 0; i < maxColumns; i++) {
            spacerRow.push({ v: '', t: 's', s: emptyStyle });
        }
        allExcelRows.push(spacerRow);
        // ═══════════════════════════════════════════════════════════
        // 1️⃣ COLUMN HEADERS ROW
        // ═══════════════════════════════════════════════════════════
        const headerRow = [
            {
                v: options?.nameLabel || 'Name',
                t: 's',
                s: nameHeaderStyle
            }
        ];
        visibleMetrics.forEach(metric => {
            headerRow.push({
                v: formatMetricLabel(metric),
                t: 's',
                s: columnHeaderStyle
            });
        });
        for (let i = headerRow.length; i < maxColumns; i++) {
            headerRow.push({ v: '', t: 's', s: columnHeaderStyle });
        }
        allExcelRows.push(headerRow);
        // ═══════════════════════════════════════════════════════════
        // 2️⃣ DATA ROWS
        // ═══════════════════════════════════════════════════════════
        rows.forEach((row, rowIndex) => {
            try {
                const isSummary = row?.isSummary === true;
                const rowName = row?.name || row?.id || `Row ${rowIndex + 1}`;
                const dataRow = [
                    {
                        v: String(rowName),
                        t: 's',
                        s: isSummary ? summaryNameStyle : nameColumnStyle
                    }
                ];
                visibleMetrics.forEach(metric => {
                    let value = '-';
                    try {
                        const rawValue = row?.data?.[metric];
                        value = formatValue(rawValue, metric);
                    }
                    catch (e) {
                        console.warn(`Error formatting metric ${metric} for row ${rowIndex}:`, e);
                        value = '-';
                    }
                    dataRow.push({
                        v: value,
                        t: 's',
                        s: isSummary ? summaryRowStyle : dataRowStyle
                    });
                });
                for (let i = dataRow.length; i < maxColumns; i++) {
                    dataRow.push({
                        v: '',
                        t: 's',
                        s: isSummary ? summaryRowStyle : dataRowStyle
                    });
                }
                allExcelRows.push(dataRow);
            }
            catch (rowError) {
                console.warn(`Error processing row ${rowIndex}:`, rowError);
            }
        });
        // ═══════════════════════════════════════════════════════════
        // ✅ CREATE EXCEL FILE
        // ═══════════════════════════════════════════════════════════
        if (allExcelRows.length <= 3) {
            console.error('❌ Export Error: No data rows generated');
            toast.info('No data rows available to export');
            return;
        }
        const worksheet = {};
        allExcelRows.forEach((row, rowIndex) => {
            row.forEach((cell, colIndex) => {
                const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                worksheet[cellRef] = cell;
            });
        });
        worksheet['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: allExcelRows.length - 1, c: maxColumns - 1 }
        });
        worksheet['!merges'] = merges;
        // Column widths
        const colWidths = [{ wch: 40 }];
        for (let i = 1; i < maxColumns; i++) {
            colWidths.push({ wch: 20 });
        }
        worksheet['!cols'] = colWidths;
        // Row heights
        const rowHeights = [];
        allExcelRows.forEach((_, index) => {
            if (index === 0)
                rowHeights.push({ hpt: 32 });
            else if (index === 1)
                rowHeights.push({ hpt: 22 });
            else if (index === 2)
                rowHeights.push({ hpt: 12 });
            else
                rowHeights.push({ hpt: 22 });
        });
        worksheet['!rows'] = rowHeights;
        // Create workbook
        const workbook = XLSX.utils.book_new();
        // ✅ Safe sheet name (max 31 chars, no special chars)
        let sheetName = safeTitle
            .replace(/[\\\/\*\?\[\]:]/g, '')
            .substring(0, 31);
        if (!sheetName)
            sheetName = 'Report';
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        // ✅ Safe filename
        const dateStr = moment().format('YYYYMMDD_HHmm');
        const safeFileName = safeTitle.replace(/[^a-zA-Z0-9]/g, '_');
        const finalFileName = `${safeFileName}_${dateStr}.xlsx`;
        XLSX.writeFile(workbook, finalFileName);
    }
    catch (error) {
        console.error('❌ Export failed:', error);
        console.error('Error details:', {
            message: error?.message,
            stack: error?.stack
        });
        toast.info(`Export failed: ${error?.message || 'Unknown error'}. Check console for details.`);
    }
};
export default exportEmailChannelExcel;
